import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { COUNTRY_CODE } from '@cemex-core/cmx-api';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { getCountry, getLegalEntity, State } from '@cemex/cmx-client-layout';
import { getActiveExperienceCode, getActiveFeatures } from '@ordersnproducts/modules/order-builder/reducers';
import { Features } from '@ordersnproducts/features';
import { ORDER_EXPERIENCES } from '../constants/order-experiences';
import { _safe } from '../utils/safe-get.method';
import { RMXCOUNTRIES } from '@ordersnproducts/modules/order-builder/modules/rms-specification/models/rmx-ul-countries.constant';
import { getUserCountry } from '@ordersnproducts/store';

export enum TemperatureFeatures {
  ICE = 'ICE',
  NITRO = 'NITRO',
}

export enum EOrdersFeature {
  SAMEDAY = 'SAMEDAY',
}

export enum ValueAddedCategoryCode {
  ICE = 'ICE',
  NITROGEN = 'CORINH',
  RETARDANT = 'RETARS',
}

export const VALUE_ADDED_SPECIALIZED_FEATURE = 'ValueAddedSpecialized';

export const BUILDING_MATERIALS_FEATURE = 'BUILDING_MATERIAL';

@Injectable({
  providedIn: 'root',
})
export class ExperiencesService {
  private countryCode: string;

  constructor(
    private appStore: Store<State>,
    private featureToggle: FeatureToggleService,
    private store: Store<State>,
  ) {}

  public isRmsEnabled(): boolean {
    this.store
      .pipe(
        select(getCountry),
        map((country) => (this.countryCode = country)),
      )
      .subscribe();

    switch (this.countryCode) {
      case COUNTRY_CODE.USA:
      case COUNTRY_CODE.UNITED_KINGDOM:
      case COUNTRY_CODE.FRANCE:
      case COUNTRY_CODE.MEXICO:
      case COUNTRY_CODE.COLOMBIA:
        return this.rmsExperienceEnabled();
      default:
        return false;
    }
  }

  public isCommandUlEnabled(): boolean {
    this.store
      .pipe(
        select(getCountry),
        map((country) => (this.countryCode = country)),
      )
      .subscribe();

    switch (this.countryCode) {
      case COUNTRY_CODE.USA:
        return this.commandUlEnabled();
      default:
        return false;
    }
  }

  public isRmsULExperienceEnabled$(): Observable<boolean> {
    const rmsULExperience$ = this.store.pipe(
      select(getCountry),
      withLatestFrom(this.rmxExperienceEnabled$()),
      switchMap(([country, rmxExperienceEnabled]) => {
        this.countryCode = _safe(() => country.trim());
        switch (this.countryCode) {
          case COUNTRY_CODE.MEXICO:
          case COUNTRY_CODE.COLOMBIA:
            return of(rmxExperienceEnabled);
          default:
            return of(false);
        }
      }),
    );
    return rmsULExperience$;
  }

  /**
   * Checks if RMX Universal Link experience is enabled in the store.
   * @returns Promise<boolean> - `true` if enabled, `false` otherwise.
   */
  public rmxExperienceEnabled$(): Observable<boolean> {
    const rmxULEnabled$ = this.store.pipe(
      select(getActiveExperienceCode),
      filter((expCode) => !!expCode),
      map((expCode) => {
        return expCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK;
      }),
    );
    return rmxULEnabled$;
  }
  /**
   * Checks if the RMX Universal Link experience is enabled for the user's country.
   * @returns Observable<boolean> - `true` if the experience is enabled for the user's country
   */
  public isRmxUlByCountry(): Observable<boolean> {
    return this.rmxExperienceEnabled$().pipe(
      withLatestFrom(this.appStore.select(getUserCountry)),
      map(([enabled, countryCode]) => {
        return enabled && RMXCOUNTRIES.includes(countryCode as COUNTRY_CODE);
      }),
    );
  }

  /**
   * verify the experience and the feature to enable the DCO 2.0 or 1.0
   */
  public experienceDcoEnabled$(): Observable<boolean> {
    const experience$ = this.store.pipe(select(getActiveExperienceCode), take(1));
    const countryCode$ = this.store.select(getCountry).pipe(take(1));
    const feature$ = this.store.select(getActiveFeatures).pipe(
      filter((features) => features != undefined),
      take(1),
    );
    const dcoEnabled$ = forkJoin([experience$, countryCode$, feature$]).pipe(
      map(([expCode, countryCode, features]) => {
        const dcoFeatureEnabled = this.featureToggle.isFeatureEnabled(Features.DCO_IMPROV);
        if (countryCode === COUNTRY_CODE.USA) {
          const command = expCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK;
          const arkik = expCode === ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK;
          const valueAddedfeature = features.find((feature) => feature.key === VALUE_ADDED_SPECIALIZED_FEATURE);
          if (command) {
            const experienceWithDco =
              command && dcoFeatureEnabled && valueAddedfeature != undefined && valueAddedfeature.enabled === true;
            return experienceWithDco;
          } else if (arkik) {
            const experienceWithDco = arkik && dcoFeatureEnabled;
            return experienceWithDco;
          }
        }
        const rmxExperience = expCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK;
        return rmxExperience && dcoFeatureEnabled;
      }),
    );
    return dcoEnabled$;
  }

  /**
   * Validates if the current experience is command 2.0
   *
   * @returns {Observable<boolean>}
   */
  public commandDcoEnabled$(): Observable<boolean> {
    const feature$ = this.store.pipe(
      select(getActiveFeatures),
      filter((features) => !!features && !!features.length),
      map((features) => {
        const commandFeature = features.find((feature) => feature.key === VALUE_ADDED_SPECIALIZED_FEATURE);
        return commandFeature !== undefined && commandFeature.enabled;
      }),
      take(1),
    );
    return feature$;
  }

  public commandDcoEnabled(): boolean {
    let commandFeature: boolean;
    this.store
      .pipe(
        select(getActiveFeatures),
        filter((features) => !!features && !!features.length),
        map((features) => {
          const featureAvailable = features.find((feature) => feature.key === VALUE_ADDED_SPECIALIZED_FEATURE);
          commandFeature = !!featureAvailable ? featureAvailable.enabled : false;
          return commandFeature;
        }),
      )
      .subscribe();
    return commandFeature;
  }

  public isSimmaUlEnabled(): boolean {
    this.store
      .pipe(
        select(getCountry),
        map((country) => (this.countryCode = country)),
      )
      .subscribe();

    switch (this.countryCode) {
      case COUNTRY_CODE.ISRAEL:
        return this.simmaUlEnabled();
      default:
        return false;
    }
  }

  /**
   * Validates if the user can create a large order in the current experience
   *
   * @returns {boolean}
   */
  public commandLargeOrderEnabled(): boolean {
    let largeOrderFeatureEnabled: boolean;
    this.store
      .select(getActiveExperienceCode)
      .pipe(
        withLatestFrom(this.store.select(getLegalEntity)),
        map(([expCode, legalEntity]) => {
          largeOrderFeatureEnabled =
            (expCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK && legalEntity.countryCode === COUNTRY_CODE.USA) ||
            (legalEntity.countryCode === COUNTRY_CODE.MEXICO && expCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK);
        }),
      )
      .subscribe();
    return largeOrderFeatureEnabled;
  }

  /**
   * Validates if the current experience is command 1.5
   *
   * @returns {Observable<boolean>}
   */
  public isCommand(): Observable<boolean> {
    return this.store.pipe(
      select(getActiveExperienceCode),
      filter((expCode) => !!expCode),
      map((expCode) => {
        return expCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK;
      }),
      take(1),
    );
  }

  private simmaOnlineFeatureToggleEnabled(): boolean {
    return this.featureToggle.isFeatureEnabled(Features.SimmaOnline);
  }

  private rmsExperienceEnabled(): boolean {
    let rmsExperience: boolean;
    this.store
      .pipe(
        select(getActiveExperienceCode),
        filter((expCode) => !!expCode),
        map((expCode) => {
          rmsExperience = expCode === ORDER_EXPERIENCES.RMX_LEGACY || expCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK;
        }),
      )
      .subscribe();
    return rmsExperience;
  }

  private commandUlEnabled(): boolean {
    let commandExperience: boolean;
    this.store
      .pipe(
        select(getActiveExperienceCode),
        filter((expCode) => !!expCode),
        map((expCode) => {
          commandExperience = expCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK;
        }),
      )
      .subscribe();
    return commandExperience;
  }

  private simmaUlEnabled(): boolean {
    let simmaExperience: boolean;
    this.store
      .pipe(
        select(getActiveExperienceCode),
        filter((expCode) => !!expCode),
        map((expCode) => {
          simmaExperience = expCode === ORDER_EXPERIENCES.SIMMA_UNIVERSAL_LINK;
        }),
      )
      .subscribe();
    return simmaExperience && this.simmaOnlineFeatureToggleEnabled();
  }

  // features for temperature
  // ICE or NITRO
  public isTemperatureFeatureEnable$(featureTemperature: TemperatureFeatures): Observable<boolean> {
    return this.isTemperatureEnabled$().pipe(
      take(1),
      map((feature) => feature === featureTemperature),
    );
  }

  public isTemperatureEnabled$(): Observable<TemperatureFeatures> {
    const experience$ = this.store.pipe(select(getActiveExperienceCode), take(1));
    const countryCode$ = this.store.select(getCountry).pipe(take(1));
    const feature$ = this.store.select(getActiveFeatures).pipe(
      filter((features) => features != undefined),
      take(1),
    );
    const temperatureEnable$ = forkJoin([experience$, countryCode$, feature$]).pipe(
      map(([expCode, countryCode, features]) => {
        if (countryCode === COUNTRY_CODE.USA && expCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK) {
          const isCommand2 = _safe(
            () => features.find((feature) => feature.key === VALUE_ADDED_SPECIALIZED_FEATURE).enabled,
            false,
          );

          const temperatureFeature = features.find(
            (feature) =>
              (feature.key === TemperatureFeatures.ICE.toString() ||
                feature.key === TemperatureFeatures.NITRO.toString()) &&
              feature.enabled === true,
          );
          return temperatureFeature != undefined && isCommand2 ? (temperatureFeature.key as TemperatureFeatures) : null;
        }
        return null;
      }),
    );
    return temperatureEnable$;
    // return of(TemperatureFeatures.NITRO)
  }

  public isArkikUlEnabled(): boolean {
    this.store
      .pipe(
        select(getCountry),
        map((country) => (this.countryCode = country)),
      )
      .subscribe();

    switch (this.countryCode) {
      case COUNTRY_CODE.USA:
        return this.arkikUlEnabled();
      default:
        return false;
    }
  }

  private arkikUlEnabled(): boolean {
    let arkikExperience: boolean;
    this.store
      .pipe(
        select(getActiveExperienceCode),
        filter((expCode) => !!expCode),
        map((expCode) => {
          arkikExperience = expCode === ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK;
        }),
      )
      .subscribe();
    return arkikExperience;
  }

  public isArkikEnabled$(): Observable<boolean> {
    const getActiveExperienceCode$ = this.store.pipe(select(getActiveExperienceCode));
    const getCountry$ = this.store.pipe(select(getCountry));
    return combineLatest([getActiveExperienceCode$, getCountry$]).pipe(
      map(([expCode, country]) => {
        return country === COUNTRY_CODE.USA && expCode === ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK;
      }),
      take(1),
    );
  }

  /**
   * Returns an observable that emits a boolean indicating whether the "Building Materials" feature is enabled
   * for the current active experience.
   *
   * The feature is considered enabled if:
   * - The feature toggle for "ENABLE_BUILDING_MATERIALS" is active.
   * - The active experience code matches `ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK`.
   * - The "BUILDING_MATERIALS_FEATURE" is present and enabled in the active features list.
   *
   * The observable emits only once when all required data is available.
   *
   * @returns {Observable<boolean>} An observable emitting `true` if the "Building Materials" feature is enabled, otherwise `false`.
   */
  public isBuildingMaterialsEnabled$(): Observable<boolean> {
    const getActiveExperienceCode$ = this.store.pipe(select(getActiveExperienceCode));
    const featureActive = this.store.pipe(select(getActiveFeatures));
    return combineLatest([getActiveExperienceCode$, featureActive]).pipe(
      filter(([expCode, features]) => !!expCode && !!features),
      map(([expCode, features]) => {
        const isEnabledBuildingMaterials = this.featureToggle.isFeatureEnabled(Features.ENABLE_BUILDING_MATERIALS);
        const isBuildingMaterialsFeatureActive = features.some(
          (feature) => feature.key === BUILDING_MATERIALS_FEATURE && feature.enabled,
        );
        return (
          isEnabledBuildingMaterials &&
          expCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK &&
          isBuildingMaterialsFeatureActive
        );
      }),
      take(1),
    );
  }
}
